const OPTIMIZATION_PROGRESS_STATUSES = new Set([
  "pending",
  "running",
  "completed",
  "failed",
  "cancelled",
]);

function normalizeOptionalTimestamp(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizeOptionalDurationMs(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0
    ? Math.round(numericValue)
    : null;
}

function normalizeProgressStep(candidate, index) {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return null;
  }

  const key = typeof candidate.key === "string" ? candidate.key.trim() : "";
  const label = typeof candidate.label === "string" ? candidate.label.trim() : "";
  if (!key || !label) {
    return null;
  }

  const normalizedStatus = typeof candidate.status === "string"
    ? candidate.status.trim().toLowerCase()
    : "pending";

  return {
    key,
    label,
    status: OPTIMIZATION_PROGRESS_STATUSES.has(normalizedStatus) ? normalizedStatus : "pending",
    startedAt: normalizeOptionalTimestamp(candidate.startedAt || candidate.started_at),
    finishedAt: normalizeOptionalTimestamp(candidate.finishedAt || candidate.finished_at),
    durationMs: normalizeOptionalDurationMs(candidate.durationMs ?? candidate.duration_ms),
    position: index + 1,
  };
}

export function sanitizeOptimizationProgress(candidate) {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return null;
  }

  const steps = (Array.isArray(candidate.steps) ? candidate.steps : [])
    .map(normalizeProgressStep)
    .filter(Boolean);
  if (!steps.length) {
    return null;
  }

  const currentStepCandidate = typeof candidate.currentStepKey === "string"
    ? candidate.currentStepKey.trim()
    : (typeof candidate.current_step_key === "string" ? candidate.current_step_key.trim() : "");
  const inferredCurrentStep = steps.find((step) => ["running", "failed", "cancelled"].includes(step.status))
    || [...steps].reverse().find((step) => step.status === "completed")
    || steps[0];
  const currentStepKey = steps.some((step) => step.key === currentStepCandidate)
    ? currentStepCandidate
    : inferredCurrentStep.key;
  const completedStepCount = steps.filter((step) => step.status === "completed").length;
  const numericVersion = Number(candidate.version);

  return {
    version: Number.isInteger(numericVersion) && numericVersion > 0 ? numericVersion : 1,
    currentStepKey,
    completedStepCount,
    totalStepCount: steps.length,
    updatedAt: normalizeOptionalTimestamp(candidate.updatedAt || candidate.updated_at),
    steps,
  };
}

export function sanitizeOptimizationProgressForStatus(candidate, jobStatus) {
  const progress = sanitizeOptimizationProgress(candidate);
  if (!progress) {
    return null;
  }

  const normalizedJobStatus = typeof jobStatus === "string"
    ? jobStatus.trim().toLowerCase()
    : "";
  if (
    normalizedJobStatus === "completed"
    && progress.steps.some((step) => step.status !== "completed")
  ) {
    return null;
  }
  if (
    normalizedJobStatus === "failed"
    && !progress.steps.some((step) => step.status === "failed")
  ) {
    return null;
  }
  if (
    normalizedJobStatus === "cancelled"
    && !progress.steps.some((step) => step.status === "cancelled")
  ) {
    return null;
  }

  return progress;
}

export function mergeOptimizationProgress(currentProgress, candidateProgress) {
  const current = sanitizeOptimizationProgress(currentProgress);
  const candidate = sanitizeOptimizationProgress(candidateProgress);
  if (!candidate) {
    return current;
  }
  if (!current) {
    return candidate;
  }

  const currentKeys = current.steps.map((step) => step.key).join("|");
  const candidateKeys = candidate.steps.map((step) => step.key).join("|");
  if (current.version !== candidate.version || currentKeys !== candidateKeys) {
    return candidate;
  }

  const currentIndex = current.steps.findIndex((step) => step.key === current.currentStepKey);
  const candidateIndex = candidate.steps.findIndex((step) => step.key === candidate.currentStepKey);
  const completedStepRegressed = current.steps.some((step, index) => (
    step.status === "completed" && candidate.steps[index]?.status !== "completed"
  ));
  const terminalStepRegressed = current.steps.some((step, index) => (
    ["failed", "cancelled"].includes(step.status)
    && candidate.steps[index]?.status !== step.status
  ));

  if (
    candidate.completedStepCount < current.completedStepCount
    || (currentIndex >= 0 && candidateIndex >= 0 && candidateIndex < currentIndex)
    || completedStepRegressed
    || terminalStepRegressed
  ) {
    return current;
  }

  return candidate;
}

export function formatOptimizationProgressDuration(durationMs) {
  const normalizedDurationMs = normalizeOptionalDurationMs(durationMs);
  if (normalizedDurationMs === null) {
    return "";
  }

  const totalSeconds = Math.max(0, Math.floor(normalizedDurationMs / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds} s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes} min ${String(seconds).padStart(2, "0")} s` : `${minutes} min`;
}

export function buildOptimizationProgressSummary(progress) {
  const normalizedProgress = sanitizeOptimizationProgress(progress);
  if (!normalizedProgress) {
    return "";
  }

  const currentIndex = normalizedProgress.steps.findIndex(
    (step) => step.key === normalizedProgress.currentStepKey,
  );
  const currentStepNumber = currentIndex >= 0
    ? currentIndex + 1
    : Math.min(normalizedProgress.completedStepCount + 1, normalizedProgress.totalStepCount);
  const remainingStepCount = Math.max(normalizedProgress.totalStepCount - currentStepNumber, 0);

  return `Étape ${currentStepNumber} sur ${normalizedProgress.totalStepCount} · ${remainingStepCount} restante${remainingStepCount === 1 ? "" : "s"}`;
}

export function buildOptimizationStopGuidance({
  canRequestCancellation = false,
  cancellationAllowed = null,
  isRemote = false,
  jobStatus = "",
  progress = null,
} = {}) {
  const normalizedStatus = typeof jobStatus === "string" ? jobStatus.trim().toLowerCase() : "";
  if (!["running", "cancelling"].includes(normalizedStatus)) {
    return null;
  }

  if (normalizedStatus === "cancelling") {
    return {
      actionLabel: "Arrêt demandé",
      canRequest: false,
      message: "Arrêt au prochain point sûr. L’étape en cours peut se terminer.",
      title: "Arrêt en cours",
    };
  }

  if (!isRemote && cancellationAllowed === false) {
    return {
      actionLabel: "Finalisation…",
      canRequest: false,
      message: "Sauvegarde finale déjà commencée; l’arrêt n’est plus disponible.",
      showAction: true,
      title: "Finalisation en cours",
    };
  }

  if (canRequestCancellation) {
    return {
      actionLabel: "Arrêter",
      canRequest: true,
      message: "Arrêt au prochain point sûr. La tentative en cours sera abandonnée; le dispatch précédent sera conservé.",
      showAction: true,
      title: "Arrêt possible maintenant",
    };
  }

  return {
    actionLabel: "Arrêt indisponible",
    canRequest: false,
    message: isRemote
      ? "Ce calcul a été lancé dans une autre session. L’arrêt doit être demandé depuis la session qui l’a lancé."
      : "L’arrêt n’est plus disponible pour ce calcul.",
    title: isRemote ? "Suivi partagé" : "Arrêt indisponible",
  };
}
